// src/pages/company/RegisterCompany.jsx

import React, { useState } from "react";
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  useTheme,
  CircularProgress,
} from "@mui/material";



import { useNavigate, Link } from "react-router-dom";
import { tokens } from "../../theme";

const RegisterCompany = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    address: "",
    tinNumber: "",
    subscriptionEndDate: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/companies`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...formData,
            subscriptionEndDate: new Date(
              formData.subscriptionEndDate
            ).toISOString(),
          }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || "Registration failed");
      }

      setSuccess("Company registered successfully!");

      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 1, sm: 2, md: 3 },   // ✅ mobile padding fix
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[400]} 100%)`
            : `linear-gradient(135deg, #f5f7fa 0%, #e4ecfb 100%)`,
      }}
    >
      <Paper
        elevation={10}
        sx={{
          width: "100%",
          maxWidth: 1000,
          borderRadius: { xs: "12px", md: "20px" },
          overflow: "hidden",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1.1fr" },
        }}
      >
        {/* LEFT PANEL */}
        <Box
          sx={{
            background:
              theme.palette.mode === "dark"
                ? `linear-gradient(180deg, ${colors.blueAccent[500]} 0%, ${colors.blueAccent[700]} 100%)`
                : `linear-gradient(180deg, ${colors.blueAccent[400]} 0%, ${colors.blueAccent[600]} 100%)`,
            color: "#fff",
            p: { xs: 3, sm: 4, md: 6 },   // ✅ responsive padding
            textAlign: { xs: "center", md: "left" },
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: "24px", sm: "32px", md: "40px" }, // ✅ responsive title
              fontWeight: "bold",
              mb: 2,
            }}
          >
            Distributor ERP
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "14px", sm: "16px", md: "18px" },
              opacity: 0.9,
              mb: 4,
            }}
          >
            Register your company and manage your full distribution system
            in one platform.
          </Typography>

          <Box sx={{ display: { xs: "none", sm: "block" } }}>
            <Typography variant="h6" mb={2}>
              Features Included
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography>✔ Sales Management</Typography>
              <Typography>✔ Inventory Control</Typography>
              <Typography>✔ Multi-user Access</Typography>
              <Typography>✔ Reports & Analytics</Typography>
            </Box>
          </Box>
        </Box>

        {/* RIGHT PANEL */}
        <Box
          sx={{
            p: { xs: 2, sm: 3, md: 5 }, // ✅ mobile padding
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: "22px", sm: "28px", md: "32px" },
              fontWeight: "bold",
            }}
          >
            Company Registration
          </Typography>

          <Typography color={colors.grey[300]} mb={3} fontSize={{ xs: 12, sm: 14 }}>
            Create your organization account
          </Typography>

          {/* ERROR / SUCCESS */}
          {error && (
            <Box sx={{ bgcolor: colors.redAccent[500], p: 2, mb: 2, borderRadius: 2, color: "#fff" }}>
              {error}
            </Box>
          )}

          {success && (
            <Box sx={{ bgcolor: colors.greenAccent[500], p: 2, mb: 2, borderRadius: 2, color: "#fff" }}>
              {success}
            </Box>
          )}

          {/* FORM */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: { xs: 2, sm: 3 }, // ✅ smaller gap on mobile
            }}
          >
            <TextField fullWidth variant="filled" label="Company Name" name="name" onChange={handleChange} />

            <TextField fullWidth variant="filled" label="Email" name="email" onChange={handleChange} />

            <TextField fullWidth variant="filled" label="Phone" name="phone" onChange={handleChange} />

            <TextField fullWidth variant="filled" type="password" label="Password" name="password" onChange={handleChange} />

            <TextField fullWidth variant="filled" label="TIN Number" name="tinNumber" onChange={handleChange} />

            <TextField fullWidth variant="filled" type="date" label="Subscription End" name="subscriptionEndDate" InputLabelProps={{ shrink: true }} onChange={handleChange} />

            <TextField
              fullWidth
              variant="filled"
              multiline
              rows={3}
              label="Address"
              name="address"
              onChange={handleChange}
              sx={{ gridColumn: "1 / -1" }}
            />

            <Button
              type="submit"
              fullWidth
              disabled={loading}
              sx={{
                gridColumn: "1 / -1",
                py: { xs: 1.2, sm: 1.7 },
                fontWeight: "bold",
                borderRadius: 2,
                backgroundColor: colors.greenAccent[500],
                color: "#fff",
              }}
            >
              {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Register Company"}
            </Button>
          </Box>

          <Typography mt={3} textAlign="center" fontSize={{ xs: 12, sm: 14 }}>
            Already have an account?{" "}
            <Link to="/login" style={{ color: colors.blueAccent[400], fontWeight: "bold" }}>
              Login
            </Link>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default RegisterCompany;